import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithAuth } from '../../utils/authUtils';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const defaultValues = {
	systolic: '',
	diastolic: '',
	heartRate: '',
	bloodSugar: '',
	temperature: '',
	weight: '',
	note: ''
};

const fields = [
	{ key: 'systolic', label: 'Huyết áp trên', unit: 'mmHg', step: 1, min: 50, max: 250 },
	{ key: 'diastolic', label: 'Huyết áp dưới', unit: 'mmHg', step: 1, min: 30, max: 180 },
	{ key: 'heartRate', label: 'Nhịp tim', unit: 'bpm', step: 1, min: 30, max: 220 },
	{ key: 'bloodSugar', label: 'Đường huyết', unit: 'mmol/L', step: 0.1, min: 0, max: 30 },
	{ key: 'temperature', label: 'Nhiệt độ', unit: '°C', step: 0.1, min: 30, max: 45 },
	{ key: 'weight', label: 'Cân nặng', unit: 'kg', step: 0.1, min: 20, max: 250 }
];

const formatNumber = (value, step) => {
	if (value === '' || value === null || value === undefined) return '';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '';
	return Number.isInteger(step) ? String(Math.round(numeric)) : numeric.toFixed(1);
};

const HealthLogForm = ({ elderlyId, initialValues, onSaved }) => {
	const [values, setValues] = useState(defaultValues);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');
	const [dirty, setDirty] = useState(false);
	const appliedInitialRef = useRef({ elderlyId: '', key: '' });

	useEffect(() => {
		if (!initialValues) return;

		const nextElderlyId = String(elderlyId ?? '');
		const nextKey = String(initialValues.id ?? initialValues.measuredAt ?? '');
		const elderlyChanged = nextElderlyId !== appliedInitialRef.current.elderlyId;

		if (!elderlyChanged && dirty) return;

		appliedInitialRef.current = { elderlyId: nextElderlyId, key: nextKey };
		setDirty(false);
		setValues({
			systolic: formatNumber(initialValues.systolic ?? '', 1),
			diastolic: formatNumber(initialValues.diastolic ?? '', 1),
			heartRate: formatNumber(initialValues.heartRate ?? '', 1),
			bloodSugar: formatNumber(initialValues.bloodSugar ?? '', 0.1),
			temperature: formatNumber(initialValues.temperature ?? '', 0.1),
			weight: formatNumber(initialValues.weight ?? '', 0.1),
			note: initialValues.note ?? ''
		});
	}, [initialValues, elderlyId, dirty]);

	useEffect(() => {
		const nextElderlyId = String(elderlyId ?? '');
		if (nextElderlyId && nextElderlyId !== appliedInitialRef.current.elderlyId) {
			setDirty(false);
		}
	}, [elderlyId]);

	const updateValue = (key, nextValue) => {
		setDirty(true);
		setValues((prev) => ({ ...prev, [key]: nextValue }));
		setMessage('');
		setError('');
	};

	const adjustValue = (key, step) => {
		const field = fields.find((item) => item.key === key);
		const currentValue = Number(values[key]);
		const baseValue = Number.isFinite(currentValue) ? currentValue : 0;
		const nextValue = baseValue + step;
		const clamped = Math.min(field.max, Math.max(field.min, nextValue));
		updateValue(key, field.step % 1 === 0 ? String(Math.round(clamped)) : clamped.toFixed(1));
	};

	const hasAnyValue = useMemo(() => {
		return Object.values(values).some((value) => value !== '' && value !== null && value !== undefined);
	}, [values]);

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!elderlyId) {
			setError('Thiếu elderlyId để lưu chỉ số.');
			return;
		}

		if (!hasAnyValue) {
			setError('Vui lòng nhập ít nhất một chỉ số sức khỏe.');
			return;
		}

		try {
			setSaving(true);
			setError('');
			setMessage('');
			const response = await fetchWithAuth(`${API_BASE}/api/health-logs`, {
				method: 'POST',
				headers: {
					
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					elderlyId,
					systolic: values.systolic === '' ? null : Number(values.systolic),
					diastolic: values.diastolic === '' ? null : Number(values.diastolic),
					heartRate: values.heartRate === '' ? null : Number(values.heartRate),
					bloodSugar: values.bloodSugar === '' ? null : Number(values.bloodSugar),
					temperature: values.temperature === '' ? null : Number(values.temperature),
					weight: values.weight === '' ? null : Number(values.weight),
					note: values.note.trim() || null,
					measuredAt: new Date().toISOString()
				})
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(payload?.message || 'Không thể lưu nhật ký sức khỏe.');
			}

			setMessage(payload?.message || 'Đã lưu chỉ số sức khỏe thành công.');
			if (typeof onSaved === 'function') {
				await onSaved(payload?.data);
			}
			setValues(defaultValues);
			setDirty(false);
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<section className="health-card health-form-card">
			<div className="health-card-header">
				<div>
					<p className="health-eyebrow">Nhật ký đo chỉ số sinh học</p>
					<h2>Ghi nhận chỉ số hôm nay</h2>
					<p className="health-form-note">Nhập nhanh bằng nút +/- hoặc gõ trực tiếp, hệ thống sẽ tự lưu theo hồ sơ đang mở.</p>
				</div>
				<span className="health-pill health-pill-soft">Input lớn cho người cao tuổi</span>
			</div>

			<form onSubmit={handleSubmit} className="health-form-grid">
				{fields.map((field) => (
					<div key={field.key} className="health-field">
						<label>{field.label}</label>
						<div className="health-stepper-row">
							<button type="button" className="health-step-btn" onClick={() => adjustValue(field.key, -field.step)}>
								−
							</button>
							<input
								type="number"
								inputMode={field.step % 1 === 0 ? 'numeric' : 'decimal'}
								step={field.step}
								min={field.min}
								max={field.max}
								value={values[field.key]}
								onChange={(event) => updateValue(field.key, event.target.value)}
								placeholder="Nhập số"
							/>
							<button type="button" className="health-step-btn" onClick={() => adjustValue(field.key, field.step)}>
								+
							</button>
						</div>
						<div className="health-field-hint">
							{field.unit ? `${field.unit} • ` : ''}{field.min} - {field.max}
						</div>
					</div>
				))}

				<div className="health-field health-field-note">
					<label>Ghi chú (tùy chọn)</label>
					<textarea
						value={values.note}
						onChange={(event) => updateValue('note', event.target.value)}
						placeholder="Ví dụ: Cảm thấy mệt, vừa ăn cơm, uống thuốc lúc 7h sáng..."
						rows="3"
						maxLength="500"
						className="health-note-input"
					/>
					<div className="health-field-hint">{values.note.length}/500 ký tự</div>
				</div>

				<div className="health-form-actions">
					<button type="submit" className="health-save-btn" disabled={saving}>
						{saving ? 'Đang lưu...' : 'Lưu chỉ số'}
					</button>
				</div>
			</form>

			{error && <p className="health-message health-message-error">{error}</p>}
			{message && <p className="health-message health-message-success">{message}</p>}
		</section>
	);
};

export default HealthLogForm;
